import { Order } from "@/types/ApiTypes";
import React from "react";
import { useTranslation } from "react-i18next";

type PrintTargetType = {
  printTarget: Order;
  contentRef: React.RefObject<HTMLDivElement>;
  arabicTime?: string;
  englishTime?: string;
};

export default function PrintOrderContent({
  printTarget,
  contentRef,
  arabicTime,
  englishTime,
}: PrintTargetType) {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || "en").split("-")[0];
  if (!printTarget) {
    return null; // Return null if printTarget is not provided
  }
  return (
    <div
      ref={contentRef}
      className="w-full text-xl print hidden print:max-w-none print:p-8"
    >
      <div className="max-w-sm mx-auto py-5 px-1 my-1 rounded-xl flex flex-col items-center space-y-2 print:space-y-4 print:p-6 print:rounded-2xl print:max-w-xl">
        {/* Header */}
        <div className="flex items-center space-x-2 print:space-x-4">
          <h2 className="text-lg font-semibold print:text-2xl">
            Order # {printTarget.orderId}
          </h2>
        </div>

        {/* Items */}
        <div className="flex items-center space-x-2 text-sm print:text-xl">
          <span>
            {printTarget?.items?.map((item, index) => {
              const isLastItem =
                index === (printTarget?.invoice?.items?.length ?? 0) - 1;

              return (
                <div key={index} className="flex flex-col items-center gap-1 print:gap-2">
                  <div className="flex items-center gap-10 justify-between">
                    <span
                      className="whitespace-normal flex flex-row-reverse gap-5"
                      dir="rtl"
                    >
                      <span dir="ltr"> {item.quantity} X </span>
                      <span>
                        <span className="flex justify-center">{item.nameEN}</span>
                        <span className="flex justify-center">
                          {item.nameAR !== item.nameEN ? item.nameAR : ""}
                        </span>
                      </span>
                    </span>
                  </div>

                  {item.addons?.length > 0 && (
                    <span className="flex flex-col items-center font-bold">
                      {t("ordersBody.Addons")}
                      <span className="font-normal flex flex-col items-center">
                        {item.addons.map((addon, i) => (
                          <span key={i}>
                            {locale === "en" ? addon.nameEN : addon.nameAR}
                          </span>
                        ))}
                      </span>
                    </span>
                  )}

                  {item.options?.length > 0 && (
                    <span className="flex flex-col items-center font-bold">
                      {t("ordersBody.Options")}
                      <span className="flex flex-col font-normal">
                        {item.options.flatMap((option) =>
                          option.options.map((optItem, i) => (
                            <span key={i}>
                              {locale === "en" ? optItem.nameEN : optItem.nameAR}
                            </span>
                          ))
                        )}
                      </span>
                    </span>
                  )}
                  {item.notes && <>
                    <div className="flex flex-col items-center ">
                      <span className="font-semibold">{t("ordersBody.ItemNote")}</span>
                      <span>{item.notes}</span>
                    </div>
                  </>}

                  {!isLastItem && (
                    <div className="border-b border-gray-300 w-full my-2" />
                  )}
                </div>
              );
            })}
          </span>
        </div>

        {/* Order Info */}
        <div className="flex text-sm print:text-xl items-center space-y-1 flex-col">
          <div>
            <span className="font-semibold">{t("orders.orderType")}:</span>{" "}
            <span>
              {printTarget.deliveryType === "receiving"
                ? t("ordersBody.Receiving")
                : printTarget.deliveryType === "dine-in"
                  ? t("ordersBody.DineIn")
                  : printTarget.deliveryType === "cashier" &&
                  t("cashierHistoryBody.cashier")}
            </span>
          </div>

          {printTarget.dineIn?.floor && printTarget.dineIn?.table && (
            <>
              <div>
                <span className="font-semibold">{t("ordersBody.Floor")}:</span>{" "}
                <span>{printTarget.dineIn.floor.number}</span>
              </div>
              <div>
                <span className="font-semibold">{t("ordersBody.Table")}:</span>{" "}
                <span>{printTarget.dineIn.table.number}</span>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-center text-sm gap-1 print:text-xl">
          <span className="font-semibold">{t("orders.receiptTime")} : </span>
          {printTarget.deliveryTiming === "afap"
            ? t("ordersBody.AsSoonAsPossible")
            : printTarget.deliveryTiming === "scheduled"
              ? t("ordersBody.Scheduled")
              : t("ordersBody.AsSoonAsPossible")}
          {printTarget.deliveryAt && (
            <span className="flex gap-1 items-center">
              <span>{t("ordersBody.ReceiveAt")}</span>
              <span className="pt-1">{printTarget.deliveryAt}</span>
            </span>
          )}
        </div>

        <div className="flex items-center justify-center text-sm gap-1 print:text-xl">
          <span className="font-semibold">
            {printTarget.type === "delivery" && t("chef.delivery")}
            {printTarget.type === "pickup" && t("chef.pickup")}
            {printTarget.type === "internal" && t("chef.internal")}
          </span>
        </div>

        <div dir="rtl" className="flex items-center space-x-2 text-sm print:text-xl">
          <span className="font-semibold">{t("orders.orderedTime")} :</span>
          <span>{locale === "en" ? englishTime : arabicTime}</span>
        </div>
      </div>
    </div>
  );
}
